/**
 * The focus ring — design/09 §5.1, §12.8.
 *
 *   "`focus-ring` — 2px outline, 2px offset, NEVER REMOVED."
 *   "Full keyboard operability, including the planning grid; visible focus ring never
 *    removed." (§12.8, G2 §2.6)
 *
 * This file is on the brand-red chrome allowlist because the light-theme focus ring
 * IS brand red — one of the four sites §5.2 rule 1 permits. It is the only reason a
 * data-region component may end up displaying brand red: the ring belongs to the
 * focus state, not to the datum.
 *
 * There is exactly one focus-ring class in the product and every interactive
 * primitive applies it. Centralising it is what makes "never removed" checkable: a
 * component that does not use `FOCUS_RING` has no focus style at all and fails the
 * keyboard audit immediately, rather than having a subtly weaker ring that nobody
 * notices until an accessibility review.
 */

/**
 * `focus-visible`, not `focus`: a mouse click on a button should not leave a ring
 * behind, but every keyboard arrival must show one. Tailwind's `outline-none` is
 * deliberately absent — there is no variant of this constant that removes the
 * outline, so `outline-none` never appears anywhere in the product.
 */
export const FOCUS_RING = [
  'focus-visible:outline',
  'focus-visible:outline-2',
  'focus-visible:outline-offset-2',
  'focus-visible:outline-focus-ring',
].join(' ');

/**
 * For elements whose overflow clips an offset ring — a grid cell against a frozen
 * column, a tab inside a scroller. The ring moves inside rather than disappearing.
 */
export const FOCUS_RING_INSET = [
  'focus-visible:outline',
  'focus-visible:outline-2',
  'focus-visible:-outline-offset-2',
  'focus-visible:outline-focus-ring',
].join(' ');

/**
 * For a container that takes focus on behalf of its children — the planning grid's
 * roving-tabindex viewport.
 */
export const FOCUS_RING_WITHIN = [
  'focus-within:outline',
  'focus-within:outline-2',
  'focus-within:outline-offset-2',
  'focus-within:outline-focus-ring',
].join(' ');
